<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useId, watch } from "vue";
import Icon from "./Icon.vue";
import { useDelayedTooltip } from "../composables/tooltip";
defineOptions({ inheritAttrs: false });
const props = withDefaults(
  defineProps<{
    icon: string;
    label: string;
    active?: boolean;
    filled?: boolean;
    size?: number;
    /** Line weight of the icon, in icon units (24 per icon). */
    stroke?: number;
    disabled?: boolean;
  }>(),
  { size: 22, stroke: 1.8 },
);
const { visible: show, enter, cancel, leave } = useDelayedTooltip();
const tooltipId = useId();
const pos = ref({ left: "0px", top: "0px" });
let keyboardFocus = false;
function position(e: PointerEvent | FocusEvent) {
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  pos.value = {
    left:
      Math.min(Math.max(r.x + r.width / 2, 65), window.innerWidth - 80) + "px",
    top:
      (r.bottom + 5 > window.innerHeight - 32 ? r.top - 32 : r.bottom + 5) +
      "px",
  };
}
function pointerEnter(e: PointerEvent) {
  if (e.pointerType === "touch" || props.disabled) return;
  position(e);
  enter();
}
function focus(e: FocusEvent) {
  if (!keyboardFocus || props.disabled) return;
  position(e);
  enter();
}
function pointerDown() {
  keyboardFocus = false;
  cancel();
}
function key(e: KeyboardEvent) {
  keyboardFocus = e.key === "Tab";
  cancel();
}
watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) cancel();
  },
);
onMounted(() => {
  window.addEventListener("pointerdown", pointerDown, true);
  window.addEventListener("keydown", key, true);
  window.addEventListener("blur", cancel);
});
onBeforeUnmount(() => {
  window.removeEventListener("pointerdown", pointerDown, true);
  window.removeEventListener("keydown", key, true);
  window.removeEventListener("blur", cancel);
});
</script>
<template>
  <button
    type="button"
    v-bind="$attrs"
    class="icon-button"
    :class="{ active, filled }"
    :aria-label="label"
    :aria-describedby="show ? tooltipId : undefined"
    :aria-pressed="active === undefined ? undefined : active"
    :disabled="disabled"
    @pointerenter="pointerEnter"
    @pointerleave="leave"
    @focus="focus"
    @blur="leave"
    @pointerdown="cancel"
    @click="cancel"
  >
    <Icon :name="icon" :size="size" :stroke="stroke" /><slot /></button
  ><Teleport to="body"
    ><Transition name="fade"
      ><span
        v-if="show"
        :id="tooltipId"
        role="tooltip"
        class="tooltip"
        :style="{ ...pos, transform: 'translateX(-50%)' }"
        >{{ label }}</span
      ></Transition
    ></Teleport
  >
</template>
